import React, { useState, useEffect } from 'react';
import { Shield, Smartphone, QrCode, Check, X, Loader2, Key } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { setup2FA, enable2FA, disable2FA, get2FAStatus } from '../services/api';
import { toast } from 'sonner';

const TwoFactorSettings = ({ userId, userName }) => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [setupModal, setSetupModal] = useState(false);
  const [disableModal, setDisableModal] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    checkStatus();
  }, [userId]);

  const checkStatus = async () => {
    try {
      const result = await get2FAStatus(userId);
      setIsEnabled(result.enabled);
    } catch (error) {
      console.error('Error checking 2FA status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async () => {
    try {
      setLoading(true);
      const result = await setup2FA(userId);
      setQrCode(result.qr_code);
      setSecret(result.secret);
      setSetupModal(true);
    } catch (error) {
      toast.error('Erro ao configurar 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleEnable = async () => {
    if (verifyCode.length !== 6) {
      toast.error('Digite o código de 6 dígitos');
      return;
    }

    setVerifying(true);
    try {
      await enable2FA(userId, verifyCode);
      setIsEnabled(true);
      setSetupModal(false);
      setVerifyCode('');
      toast.success('Autenticação em 2 etapas ativada!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
    } finally {
      setVerifying(false);
    }
  };

  const handleDisable = async () => {
    if (disableCode.length !== 6) {
      toast.error('Digite o código de 6 dígitos');
      return;
    }

    setVerifying(true);
    try {
      await disable2FA(userId, disableCode);
      setIsEnabled(false);
      setDisableModal(false);
      setDisableCode('');
      toast.success('Autenticação em 2 etapas desativada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-purple-100">
            <Shield className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-800">Autenticação em 2 Etapas</h3>
            <p className="text-sm text-slate-500">Proteção extra para sua conta</p>
          </div>
        </div>

        <div className={`p-4 rounded-lg ${isEnabled ? 'bg-green-50 border border-green-200' : 'bg-slate-50 border border-slate-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isEnabled ? (
                <Check className="w-5 h-5 text-green-600" />
              ) : (
                <X className="w-5 h-5 text-slate-400" />
              )}
              <div>
                <p className={`font-medium ${isEnabled ? 'text-green-800' : 'text-slate-700'}`}>
                  {isEnabled ? 'Ativada' : 'Desativada'}
                </p>
                <p className="text-sm text-slate-500">
                  {isEnabled 
                    ? 'Sua conta está protegida com Google Authenticator' 
                    : 'Ative para adicionar uma camada extra de segurança'}
                </p>
              </div>
            </div>
            
            {isEnabled ? (
              <Button
                variant="outline"
                onClick={() => setDisableModal(true)}
                className="text-red-500 hover:text-red-600 border-red-200 hover:border-red-300"
              >
                Desativar
              </Button>
            ) : (
              <Button
                onClick={handleSetup}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Smartphone className="w-4 h-4 mr-2" />
                Ativar 2FA
              </Button>
            )}
          </div>
        </div>

        {!isEnabled && (
          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">
              <strong>Recomendado:</strong> A autenticação em 2 etapas protege sua conta mesmo se alguém descobrir sua senha.
            </p>
          </div>
        )}
      </div>

      {/* Setup Modal */}
      <Dialog open={setupModal} onOpenChange={setSetupModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5 text-purple-600" />
              Configurar Autenticação em 2 Etapas
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="text-sm text-slate-600">
              <p className="mb-2"><strong>Passo 1:</strong> Instale o app Google Authenticator no seu celular</p>
              <p><strong>Passo 2:</strong> Escaneie o QR code abaixo com o app</p>
            </div>

            {qrCode && (
              <div className="flex justify-center p-4 bg-white border rounded-lg">
                <img src={qrCode} alt="QR Code" className="w-48 h-48" />
              </div>
            )}

            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 mb-1">Código manual (se não conseguir escanear):</p>
              <p className="font-mono text-sm break-all select-all">{secret}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <strong>Passo 3:</strong> Digite o código de 6 dígitos do app
              </label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="pl-10 text-center text-xl tracking-widest font-mono"
                  maxLength={6}
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setSetupModal(false);
                  setVerifyCode('');
                }}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleEnable}
                disabled={verifyCode.length !== 6 || verifying}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
              >
                {verifying ? 'Verificando...' : 'Ativar 2FA'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Disable Modal */}
      <Dialog open={disableModal} onOpenChange={setDisableModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Shield className="w-5 h-5" />
              Desativar Autenticação em 2 Etapas
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                <strong>Atenção:</strong> Desativar a autenticação em 2 etapas deixará sua conta menos segura.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Digite o código do Google Authenticator para confirmar
              </label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="pl-10 text-center text-xl tracking-widest font-mono"
                  maxLength={6}
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setDisableModal(false);
                  setDisableCode('');
                }}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleDisable}
                disabled={disableCode.length !== 6 || verifying}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {verifying ? 'Verificando...' : 'Desativar 2FA'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TwoFactorSettings;
